import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import canOpenTenantSettings from "~/helpers/canOpenTenantSettings";
import { type TranslateKey } from "~/helpers/localizedText";
import {
	colorForModule,
	iconForModule,
	loadRecentDestinations,
	nowKey,
	topRecentDestinations
} from "~/helpers/recentDestinations";
import filterCommands, { type CommandDefinition } from "~/helpers/search/commandCatalog";
import deriveSearchActionRows, { type SearchActionRow } from "~/helpers/search/deriveSearchActionRows";
import { type EntitySearchIntent } from "~/helpers/search/entitySearchQuery";
import searchPages, {
	getPageSearchIndex,
	type PageSearchEntry,
	pageSearchLabel
} from "~/helpers/search/pageSearchIndex";
import { useAppSelector } from "~/hooks/reduxHooks";
import useCanManageTenantSettings from "~/hooks/useCanManageTenantSettings";
import useJevPageSuggestions from "~/hooks/useJevPageSuggestions";
import useEntitySearch, { type SearchResultRow, type SearchSection } from "~/layout/useEntitySearch";
import { resolveLocationMapLabel } from "~/layout/useModuleBranding";
import whatsNew from "~/pages/home/helpers/whatsNew";
import WhatsNewContext from "~/pages/home/WhatsNewContext";

export const GLOBAL_SEARCH_TYPING_DELAY_MS = 150;

const PAGE_RESULTS_LIMIT = 8;
const RECENTS_LIMIT = 8;
const COMMAND_RESULTS_LIMIT = 10;
const COMMAND_ROW_COLOR = "primary.main";

const emptySection = (key: string, title: string, rows: SearchResultRow[]): SearchSection => ({
	key,
	loading: false,
	rows,
	skeletonCount: 0,
	title
});

const toPageRow = (entry: PageSearchEntry, key: string, t: TranslateKey): SearchResultRow => ({
	color: entry.color,
	icon: entry.icon,
	key,
	label: pageSearchLabel(entry, t),
	path: entry.path,
	secondary: entry.path
});

export interface UseGlobalSearchReturn {
	actionRows: SearchActionRow[];
	hasActiveGrid: boolean;
	intent: EntitySearchIntent;
	isLoadingResults: boolean;
	itemSuggestion: null | string;
	navigableRows: SearchResultRow[];
	query: string;
	sections: SearchSection[];
}

const useGlobalSearch = (
	inputValue: string,
	active: boolean,
	commands: CommandDefinition[] = []
): UseGlobalSearchReturn => {
	const { t, i18n } = useTranslation("common");
	const whatsNewHome = useContext(WhatsNewContext);
	const canManageSettings = useCanManageTenantSettings();
	const hasActiveGrid = useAppSelector((state) => state.fernieAction.activeGridDataKey !== null);
	const query = inputValue.trim();

	const entitySearch = useEntitySearch(inputValue, active);
	const { intent, itemSuggestion } = entitySearch;

	const commandRows = useMemo<SearchResultRow[]>(() => {
		if (!intent.command) {
			return [];
		}
		return filterCommands(commands, intent.query, COMMAND_RESULTS_LIMIT).map((command) => ({
			color: command.color ?? COMMAND_ROW_COLOR,
			icon: command.icon,
			key: `command-${command.id}`,
			label: command.label,
			path: "",
			run: command.run,
			secondary: command.category,
			shortcutActionId: command.shortcutActionId
		}));
	}, [intent.command, intent.query, commands]);

	const recentRows = useMemo<SearchResultRow[]>(() => {
		if (!active || query) {
			return [];
		}
		return topRecentDestinations(
			loadRecentDestinations().filter((destination) => canOpenTenantSettings(destination.path, canManageSettings)),
			nowKey(),
			RECENTS_LIMIT
		).map((destination) => ({
			color: colorForModule(destination.module),
			icon: destination.icon ?? iconForModule(destination.module),
			key: destination.path,
			label: resolveLocationMapLabel(destination.path, destination.label, destination.labelKey, t),
			path: destination.path,
			secondary: destination.path
		}));
	}, [active, query, t, canManageSettings]);

	const openablePages = useMemo(
		() => getPageSearchIndex().filter((entry) => canOpenTenantSettings(entry.path, canManageSettings)),
		[canManageSettings]
	);

	const pageRows = useMemo<SearchResultRow[]>(() => {
		if (!query) {
			return [];
		}
		return searchPages(query, PAGE_RESULTS_LIMIT, t, openablePages).map((entry) => toPageRow(entry, entry.path, t));
	}, [query, t, openablePages]);

	const pageCandidates = useMemo(
		() => openablePages.map((entry) => ({ id: entry.path, label: pageSearchLabel(entry, t) })),
		[openablePages, t]
	);
	const shownPageIds = useMemo(() => pageRows.map((row) => row.path), [pageRows]);
	const jevPageMatches = useJevPageSuggestions({
		pages: pageCandidates,
		search: query,
		shownPageIds,
		skip: !active || intent.command || intent.explicit
	});
	const jevPageRows = useMemo(() => {
		const pagesByPath = new Map(openablePages.map((entry) => [entry.path, entry]));
		return jevPageMatches.flatMap((match) => {
			const entry = pagesByPath.get(match.pageId);
			return entry ? [toPageRow(entry, `jev-page-${entry.path}`, t)] : [];
		});
	}, [jevPageMatches, openablePages, t]);

	const whatsNewRows = useMemo<SearchResultRow[]>(() => {
		if (!active || !query || !whatsNewHome?.visible || !whatsNewHome.latest) return [];
		const currentIds = new Set(whatsNewHome.latest.features.map((feature) => feature.id));
		return whatsNewHome.tabs
			.flatMap((tab) =>
				tab.items
					.filter((item) => currentIds.has(item.feature.id))
					.map((item) => ({
						color: "primary.main",
						icon: "new_releases",
						key: `whats-new-${item.feature.id}`,
						label: t("home.whatsNew.searchEntry", {
							title: whatsNew.pickLocalizedText(item.feature.title, i18n.language)
						}),
						path: whatsNew.buildSelectionPath(tab, item.feature.key, item.releaseLabel)
					}))
			)
			.filter((row) => row.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
	}, [active, query, whatsNewHome?.visible, whatsNewHome?.latest, whatsNewHome?.tabs, t, i18n.language]);
	const sections = useMemo<SearchSection[]>(() => {
		if (intent.command) {
			return commandRows.length ? [emptySection("commands", t("search.sections.commands"), commandRows)] : [];
		}
		if (!query) {
			return recentRows.length ? [emptySection("recent", t("search.sections.recent"), recentRows)] : [];
		}
		const { orders, customers, customerSuggestions, items, itemSuggestions } = entitySearch.sections;
		const updates = whatsNewRows.length ? [emptySection("whats-new", t("home.whatsNew.viewTitle"), whatsNewRows)] : [];
		const pageSections = [
			emptySection("pages", t("search.sections.pages"), pageRows),
			...(jevPageRows.length ? [emptySection("pageSuggestions", t("search.sections.suggestions"), jevPageRows)] : [])
		];
		const pages = pageRows.length ? pageSections : [];
		const suggestedOnlyPages = !pageRows.length && jevPageRows.length ? pageSections : [];
		const ordered = intent.explicit
			? [orders, customers, customerSuggestions, items, itemSuggestions]
			: intent.isOrderNumber
				? [orders, ...updates, ...pages, customers, customerSuggestions, items, itemSuggestions, ...suggestedOnlyPages]
				: [...updates, ...pages, orders, customers, customerSuggestions, items, itemSuggestions, ...suggestedOnlyPages];
		return ordered.filter((section): section is SearchSection => section !== null);
	}, [intent, commandRows, query, recentRows, pageRows, jevPageRows, whatsNewRows, entitySearch, t]);

	const navigableRows = useMemo(() => sections.flatMap((section) => section.rows), [sections]);
	const isLoadingResults = useMemo(() => sections.some((section) => section.loading), [sections]);
	const actionRows = useMemo(
		() =>
			deriveSearchActionRows({ explicit: intent.explicit, hasActiveGrid, query }).map((row) => ({
				...row,
				label: t("search.filterGrid", { term: row.term })
			})),
		[hasActiveGrid, intent.explicit, query, t]
	);

	return { actionRows, hasActiveGrid, intent, isLoadingResults, itemSuggestion, navigableRows, query, sections };
};

export default useGlobalSearch;
